import { useState, useRef } from 'react';
import { ContactSettings, CustomSection } from '@/hooks/useContactSettings';

interface ContactEditorProps {
  settings: ContactSettings;
  onSave: (s: ContactSettings) => void;
}

type EditorTab = 'info' | 'image' | 'sections' | 'emails' | 'api';

const PHONE_ICONS = [
  'ri-whatsapp-line', 'ri-phone-line', 'ri-smartphone-line',
  'ri-message-2-line', 'ri-chat-3-line', 'ri-customer-service-2-line',
];

export default function ContactEditor({ settings, onSave }: ContactEditorProps) {
  const [form, setForm] = useState<ContactSettings>(JSON.parse(JSON.stringify(settings)));
  const [activeTab, setActiveTab] = useState<EditorTab>('info');
  const [saved, setSaved] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const update = (field: keyof ContactSettings, value: unknown) => {
    setForm(p => ({ ...p, [field]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    onSave(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const addEmail = () => {
    const email = newEmail.trim();
    if (!email || !email.includes('@') || form.recipientEmails.includes(email)) return;
    setForm(p => ({ ...p, recipientEmails: [...p.recipientEmails, email] }));
    setNewEmail('');
    setSaved(false);
  };

  const removeEmail = (i: number) => {
    setForm(p => ({ ...p, recipientEmails: p.recipientEmails.filter((_, idx) => idx !== i) }));
    setSaved(false);
  };

  const updateSection = (i: number, field: keyof CustomSection, value: string) => {
    setForm(p => {
      const sections = [...p.customSections];
      sections[i] = { ...sections[i], [field]: value };
      return { ...p, customSections: sections };
    });
    setSaved(false);
  };

  const addSection = () => {
    setForm(p => ({ ...p, customSections: [...p.customSections, { title: '', content: '' }] }));
  };

  const removeSection = (i: number) => {
    setForm(p => ({ ...p, customSections: p.customSections.filter((_, idx) => idx !== i) }));
  };

  const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      update('imageUrl', ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const tabs: { id: EditorTab; label: string; icon: string }[] = [
    { id: 'info', label: 'Informações', icon: 'ri-information-line' },
    { id: 'image', label: 'Imagem', icon: 'ri-image-line' },
    { id: 'sections', label: 'Seções', icon: 'ri-stack-line' },
    { id: 'emails', label: 'E-mails', icon: 'ri-mail-settings-line' },
    { id: 'api', label: 'API E-mail', icon: 'ri-key-2-line' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
            Editar Página Fale Conosco
          </h2>
          <p className="text-foreground-500 text-sm mt-1">
            Personalize completamente a página de contato.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setForm(JSON.parse(JSON.stringify(settings)))}
            className="px-4 py-2 text-sm text-foreground-600 border border-background-300 rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Descartar
          </button>
          <button
            onClick={handleSave}
            className={`px-4 py-2 text-sm font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
              saved ? 'bg-green-500 text-white' : 'bg-primary-500 hover:bg-primary-600 text-white'
            }`}
          >
            {saved ? (
              <><i className="ri-checkbox-circle-line"></i> Salvo!</>
            ) : (
              <><i className="ri-save-line"></i> Salvar alterações</>
            )}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-background-200 pb-0">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-t-lg cursor-pointer transition-colors whitespace-nowrap border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-600 bg-primary-50'
                : 'border-transparent text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
            }`}
          >
            <i className={`${tab.icon} text-sm`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── INFO TAB ── */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Título da Página</label>
              <input type="text" value={form.pageTitle} onChange={e => update('pageTitle', e.target.value)} className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white font-heading font-bold" />
            </div>
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Subtítulo</label>
              <textarea value={form.pageSubtitle} onChange={e => update('pageSubtitle', e.target.value)} rows={3} maxLength={300} className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white resize-none" />
              <p className="text-xs text-foreground-400 text-right mt-0.5">{form.pageSubtitle.length}/300</p>
            </div>
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Texto de Apresentação</label>
              <textarea value={form.description} onChange={e => update('description', e.target.value)} rows={4} maxLength={500} className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white resize-none" />
            </div>
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">E-mail de Contato</label>
              <input type="email" value={form.emailAddress} onChange={e => update('emailAddress', e.target.value)} className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white" />
            </div>
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Tempo de Resposta</label>
              <input type="text" value={form.responseTime} onChange={e => update('responseTime', e.target.value)} className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white" />
            </div>
          </div>

          {/* Telefone / WhatsApp */}
          <div className="space-y-4 border border-background-200 rounded-xl p-5 bg-background-50">
            <h3 className="text-xs font-bold text-foreground-500 uppercase tracking-wide">Telefone / WhatsApp</h3>
            <div>
              <label className="block text-xs text-foreground-500 mb-1">Ícone</label>
              <div className="flex items-center gap-3 mb-2">
                {PHONE_ICONS.map(icon => (
                  <button
                    key={icon}
                    onClick={() => update('phoneIcon', icon)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                      form.phoneIcon === icon ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                    }`}
                    title={icon.replace('ri-', '').replace(/-line/, '')}
                  >
                    <i className={`${icon}`}></i>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs text-foreground-500 mb-1">Label (ex: WhatsApp, Telefone)</label>
              <input type="text" value={form.phoneLabel} onChange={e => update('phoneLabel', e.target.value)} className="w-full border border-background-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-400 bg-white" />
            </div>
            <div>
              <label className="block text-xs text-foreground-500 mb-1">Número (com DDD, ex: 11999998888)</label>
              <input type="text" value={form.phoneNumber} onChange={e => update('phoneNumber', e.target.value)} placeholder="11999998888" className="w-full border border-background-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-400 bg-white" />
            </div>
            {form.phoneNumber && (
              <div className="bg-background-100 rounded-lg px-3 py-2 flex items-center gap-2 text-xs">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100">
                  <i className={`${form.phoneIcon} text-primary-500 text-sm`}></i>
                </div>
                <span className="text-foreground-700">{form.phoneLabel}: {form.phoneNumber}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── IMAGE TAB ── */}
      {activeTab === 'image' && (
        <div className="space-y-5 max-w-xl">
          <div>
            <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Imagem da Página</label>
            <p className="text-foreground-400 text-xs mb-3">Aparece ao lado das informações de contato, no desktop.</p>
            {form.imageUrl && (
              <div className="w-full aspect-[4/3] rounded-xl overflow-hidden border border-background-200 mb-3">
                <img src={form.imageUrl} alt="preview" className="w-full h-full object-cover object-top" />
              </div>
            )}
            <input type="text" value={form.imageUrl} onChange={e => update('imageUrl', e.target.value)} placeholder="URL da imagem..." className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white mb-3" />
            <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-background-300 rounded-lg py-4 text-xs text-foreground-400 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer flex items-center justify-center gap-2">
              <i className="ri-upload-cloud-2-line text-lg"></i> Upload de imagem
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleImageFile} className="hidden" />
          </div>
        </div>
      )}

      {/* ── SECTIONS TAB ── */}
      {activeTab === 'sections' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-foreground-500 text-sm">Adicione seções de conteúdo personalizadas (ex: horário de atendimento, endereço, etc).</p>
            <span className="text-xs text-foreground-400">{form.customSections.length} seções</span>
          </div>
          <div className="space-y-4">
            {form.customSections.map((section, i) => (
              <div key={i} className="border border-background-200 rounded-xl p-5 bg-background-50 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-foreground-500 uppercase tracking-wide">Seção {i + 1}</span>
                  <button onClick={() => removeSection(i)} className="w-7 h-7 flex items-center justify-center text-foreground-400 hover:text-red-500 cursor-pointer transition-colors">
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
                <div>
                  <label className="block text-xs text-foreground-500 mb-1">Título</label>
                  <input type="text" value={section.title} onChange={e => updateSection(i, 'title', e.target.value)} placeholder="Ex: Horário de Atendimento" className="w-full border border-background-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-400 bg-white font-bold" />
                </div>
                <div>
                  <label className="block text-xs text-foreground-500 mb-1">Conteúdo</label>
                  <textarea value={section.content} onChange={e => updateSection(i, 'content', e.target.value)} rows={3} maxLength={500} className="w-full border border-background-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-400 bg-white resize-none" />
                </div>
              </div>
            ))}
            {form.customSections.length < 8 && (
              <button onClick={addSection} className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-700 cursor-pointer font-bold border-2 border-dashed border-primary-200 rounded-xl px-4 py-3 w-full justify-center hover:border-primary-400 transition-colors">
                <i className="ri-add-line"></i> Adicionar seção
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── EMAILS TAB ── */}
      {activeTab === 'emails' && (
        <div className="space-y-5 max-w-xl">
          <div>
            <p className="text-foreground-500 text-sm mb-4">
              E-mails que receberão as mensagens do formulário de contato. Pelo menos um é obrigatório.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <input
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEmail())}
                placeholder="novo@email.com"
                className="flex-1 border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
              />
              <button
                onClick={addEmail}
                className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-bold px-4 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line mr-1"></i> Adicionar
              </button>
            </div>
            <div className="space-y-2">
              {form.recipientEmails.length === 0 && (
                <p className="text-foreground-400 text-xs italic">Nenhum e-mail cadastrado. Adicione pelo menos um.</p>
              )}
              {form.recipientEmails.map((email, i) => (
                <div key={i} className="flex items-center justify-between bg-background-100 rounded-lg px-4 py-2.5 border border-background-200">
                  <div className="flex items-center gap-2">
                    <i className="ri-mail-line text-primary-500"></i>
                    <span className="text-foreground-700 text-sm">{email}</span>
                  </div>
                  <button onClick={() => removeEmail(i)} className="w-7 h-7 flex items-center justify-center text-foreground-400 hover:text-red-500 cursor-pointer transition-colors">
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── API KEY TAB ── */}
      {activeTab === 'api' && (
        <div className="space-y-5 max-w-xl">
          <div className="border border-background-200 rounded-xl p-5 bg-background-50 space-y-4">
            <div className="flex items-center gap-2">
              <i className="ri-key-2-line text-primary-500 text-lg"></i>
              <h3 className="text-xs font-bold text-foreground-500 uppercase tracking-wide">Resend API Key</h3>
            </div>
            <p className="text-foreground-500 text-sm">
              A API Key do Resend é usada para disparar e-mails automaticamente sempre que alguém enviar o formulário de contato.
              Os e-mails serão enviados para todos os destinatários configurados na aba "E-mails".
            </p>
            <div>
              <label className="block text-xs text-foreground-500 mb-1.5">Chave da API</label>
              <input
                type="password"
                value={form.resendApiKey}
                onChange={e => update('resendApiKey', e.target.value)}
                placeholder="re_xxxxxxxxxxxx"
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white font-mono"
              />
              <p className="text-xs text-foreground-400 mt-1.5">
                Crie sua API Key gratuita em{" "}
                <a href="https://resend.com/api-keys" target="_blank" rel="nofollow noopener noreferrer" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">
                  resend.com/api-keys
                </a>
                {" "}— 100 e-mails/dia grátis.
              </p>
            </div>
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <i className="ri-information-line text-amber-500 mt-0.5"></i>
              <div className="text-xs text-amber-700">
                <p className="font-bold mb-1">Importante:</p>
                <p>No Resend, adicione seu domínio de envio ou use o domínio de teste <code className="bg-amber-100 px-1 rounded">resend.dev</code> para começar.</p>
                <p className="mt-1">Sem a API Key configurada, as mensagens ficam salvas apenas no banco de dados, sem notificação por e-mail.</p>
              </div>
            </div>
            {form.resendApiKey && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <i className="ri-checkbox-circle-line text-green-500"></i>
                <p className="text-xs text-green-700 font-bold">API Key configurada — os e-mails serão disparados automaticamente.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview */}
      <div className="border-t border-background-200 pt-6">
        <h3 className="text-xs font-bold text-foreground-500 uppercase tracking-wide mb-4">Pré-visualização</h3>
        <div className="bg-background-100 rounded-xl p-6 border border-background-200 space-y-4">
          <p className="font-heading font-bold text-foreground-950 text-lg">{form.pageTitle || '—'}</p>
          <p className="text-foreground-600 text-sm">{form.pageSubtitle || '—'}</p>
          <p className="text-foreground-500 text-xs">{form.description || '—'}</p>
          <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1"><i className="ri-mail-line text-primary-500"></i> {form.emailAddress || '—'}</span>
            {form.phoneNumber && (
              <span className="flex items-center gap-1"><i className={`${form.phoneIcon} text-primary-500`}></i> {form.phoneLabel}: {form.phoneNumber}</span>
            )}
            <span className="flex items-center gap-1"><i className="ri-time-line text-primary-500"></i> {form.responseTime || '—'}</span>
          </div>
          {form.customSections.length > 0 && (
            <div className="border-t border-background-200 pt-3 space-y-2">
              {form.customSections.map((s, i) => (
                <div key={i}>
                  <p className="text-xs font-bold text-foreground-700">{s.title || '—'}</p>
                  <p className="text-xs text-foreground-500">{s.content || '—'}</p>
                </div>
              ))}
            </div>
          )}
          <div className="text-xs text-foreground-400 pt-2 border-t border-background-200">
            Envios para: {form.recipientEmails.join(', ') || 'nenhum'}
          </div>
        </div>
      </div>
    </div>
  );
}